import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';

type Project = Tables<'projects'>;
type ProjectInsert = TablesInsert<'projects'>;
type ProjectUpdate = TablesUpdate<'projects'>;

export const fetchProjectsFromDb = async (): Promise<Project[]> => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as Project[];
};

export const useProjects = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuthContext();

  const { data: projects = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjectsFromDb,
    enabled: !!user && !authLoading,
  });

  const createProject = async (project: Omit<ProjectInsert, 'created_by'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('projects')
        .insert({ ...project, created_by: user?.id })
        .select()
        .single();

      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Projekt erstellt', description: 'Das Projekt wurde erfolgreich erstellt.' });
      return data;
    } catch (error: any) {
      console.error('Error creating project:', error);
      toast({ variant: 'destructive', title: 'Fehler', description: 'Projekt konnte nicht erstellt werden.' });
      throw error;
    }
  };

  const updateProject = async (id: string, updates: ProjectUpdate) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Projekt aktualisiert', description: 'Die Änderungen wurden gespeichert.' });
      return data;
    } catch (error: any) {
      console.error('Error updating project:', error);
      toast({ variant: 'destructive', title: 'Fehler', description: 'Projekt konnte nicht aktualisiert werden.' });
      throw error;
    }
  };

  const deleteProject = async (id: string) => {
    try {
      const { error } = await supabase.from('projects').delete().eq('id', id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Projekt gelöscht', description: 'Das Projekt wurde erfolgreich gelöscht.' });
    } catch (error: any) {
      console.error('Error deleting project:', error);
      toast({ variant: 'destructive', title: 'Fehler', description: 'Projekt konnte nicht gelöscht werden.' });
      throw error;
    }
  };

  return {
    projects,
    loading,
    fetchProjects: refetch,
    createProject,
    updateProject,
    deleteProject,
  };
};
